if (!customElements.get('upsell-modal')) {
  customElements.define(
    'upsell-modal',
    class UpsellModal extends DrawerComponent {
      constructor() {
        super();
        this.classesToRemoveOnLoad = 'drawer--loading';
        this.inlineErrorClass = 'upsell__inline-error';
        this.requestTimeoutMs = 12000;
        this.drawerBody = this.querySelector(this.selector);
        this.cartUpdateUnsubscriber = null;
        this.cartRefreshTimeout = null;
        this.getSectionToRenderListener = this.getSectionToRender.bind(this);
        this.lastAddedVariantId = null;
        this.latestCartData = null;
        this.onUpsellCartActionClickListener = this.onUpsellCartActionClick.bind(this);
        this.onUpsellCartQuantityChangeListener = this.onUpsellCartQuantityChange.bind(this);
      }

      get selector() {
        return '.upsell__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-upsell'));
      }

      connectedCallback() {
        super.connectedCallback();
        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.handleCartUpdate.bind(this)
        );
        document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
        this.addEventListener('click', this.onUpsellCartActionClickListener);
        this.addEventListener('change', this.onUpsellCartQuantityChangeListener);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
        this.removeEventListener('click', this.onUpsellCartActionClickListener);
        this.removeEventListener('change', this.onUpsellCartQuantityChangeListener);
        if (this.cartRefreshTimeout) {
          clearTimeout(this.cartRefreshTimeout);
        }
      }

      getSectionToRender(event) {
        const sectionId = this.getUpsellSectionId();
        if (!sectionId) return;
        if (!event?.detail?.sections) return;
        if (!event.detail.sections.includes(sectionId)) {
          event.detail.sections.push(sectionId);
        }
      }

      prepareToShow() {
        super.prepareToShow();
        this.lastAddedVariantId = null;
        this.updateUrlParam();
        this.loadUpsell();
      }

      onButtonClick(event) {
        const trigger = event.currentTarget;
        if (!this.open && trigger?.hasAttribute('data-upsell-add-trigger')) {
          this.addTriggeredProductToCart(trigger);
        }

        super.onButtonClick(event);
      }

      handleAfterHide() {
        super.handleAfterHide();
        const drawerContent = this.querySelector(this.selector);
        drawerContent.innerHTML = '';
        this.classList.add(this.classesToRemoveOnLoad);
        this.clearInlineError();
        this.restoreUrl();
      }

      getUpsellSectionId() {
        let sectionId = FoxTheme.UpsellSectionId || false;

        if (!sectionId) {
          const upsellSection = document.querySelector('.section-group-overlay-upsell');
          if (upsellSection) {
            sectionId = FoxTheme.utils.getSectionId(upsellSection);
          }
          FoxTheme.UpsellSectionId = sectionId;
        }

        return sectionId;
      }

      loadUpsell() {
        const drawerContent = this.querySelector(this.selector);
        const sectionUrl = this.getSectionUrl();
        this.clearInlineError();
        if (!sectionUrl) {
          this.showError('Unable to load upsell content. Please try again.');
          this.classList.remove(this.classesToRemoveOnLoad);
          return;
        }

        this.fetchWithTimeout(sectionUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load content: ${response.status}`);
            }
            return response.text();
          })
          .then((responseText) => {
            const productElement = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.selector);

            if (!productElement) {
              throw new Error('Invalid content received');
            }

            this.setInnerHTML(drawerContent, productElement.innerHTML);
            this.prepareCartListState(this);
            this.syncCurrentItemMirror();
            this.syncSummaryItems();
            FoxTheme.a11y.trapFocus(this, this.focusElement);
          })
          .catch((e) => {
            console.error(e);
            this.showError('Unable to load upsell content. Please refresh the page.');
            this.classList.remove(this.classesToRemoveOnLoad);
            if (drawerContent && !drawerContent.innerHTML.trim()) {
              drawerContent.innerHTML = `<div class="${this.inlineErrorClass} alert alert--error" role="alert">Unable to load upsell content. Please refresh the page and try again.</div>`;
            }
          });
      }

      getSectionUrl() {
        const sectionId = this.getUpsellSectionId();
        const productUrl = this.dataset.productUrl;
        const upsellId = this.dataset.upsellProductId;
      
        if (!sectionId || !productUrl) return null;

        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
      
        let url = `${productUrl.split('?')[0]}?section_id=${sectionId}&upsell=${encodeURIComponent(upsellId || '')}`;
        
        if (variantId) {
          url += `&variant=${variantId}`;
        }
        
        return url;
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = innerHTML;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        this.classList.remove(this.classesToRemoveOnLoad);
        this.initStickyBarSwiper();
      }

      updateUrlParam() {
        const upsellId = this.dataset.upsellProductId;
        if (!upsellId) return;
      
        const url = new URL(window.location.href);
        url.searchParams.delete('upsell');
      
        url.searchParams.set('upsell', upsellId);
        window.history.replaceState({}, '', url);
      }
      
      restoreUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('upsell');
        window.history.replaceState({}, '', url);
      }

      async addTriggeredProductToCart(trigger) {
        const payload = this.getAddPayload(trigger);
        if (!payload || trigger.getAttribute('aria-disabled') === 'true') return;

        trigger.setAttribute('aria-disabled', 'true');
        trigger.classList.add('btn--loading');

        try {
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
          );

          const config = FoxTheme.utils.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', payload.variantId);
          formData.append('quantity', `${payload.quantity}`);
          formData.append('sections', sectionsToBundle);
          formData.append('sections_url', window.location.pathname);
          config.body = formData;

          const response = await this.fetchWithTimeout(`${FoxTheme.routes.cart_add_url}`, config);
          
          if (!response.ok) {
            throw new Error('Network error. Please check your connection and try again.');
          }

          const parsedState = await response.json();
          
          if (parsedState.status) {
            const errorMessage = parsedState.description || parsedState.message || 'Unable to add item to cart.';
            throw new Error(errorMessage);
          }
          
          this.lastAddedVariantId = `${payload.variantId}`;

          const cartJson = await (
            await this.fetchWithTimeout(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
          ).json();

          cartJson.sections = parsedState.sections;
          
          // Update aria-live region for accessibility
          this.announceCartUpdate(parsedState.product_title, payload.quantity, cartJson.item_count);
          
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: {
                source: 'upsell-popup',
                product: parsedState,
                triggerProductId: `${this.dataset.upsellProductId || ''}`,
                variantId: `${payload.variantId}`,
                quantity: payload.quantity,
              },
            })
          );
        } catch (error) {
          console.error('Upsell add to cart error:', error);
          
          // Show user-facing error message
          this.showError(error.message || 'Unable to add product to cart. Please try again.');
          
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'upsell-modal-trigger',
            productVariantId: payload.variantId,
            message: error.message,
          });
        } finally {
          trigger.classList.remove('btn--loading');
          trigger.removeAttribute('aria-disabled');
        }
      }

      /**
       * Extract variant and quantity from trigger element or form
       * @param {HTMLElement} trigger - Button or element that triggered add to cart
       * @returns {Object|null} Payload with variantId and quantity, or null if invalid
       */
      getAddPayload(trigger) {
        const form = trigger?.form || trigger?.closest('form');
        const variantInput =
          form?.querySelector('[name="id"]') ||
          trigger?.closest('[data-product-form-container]')?.querySelector('[name="id"]');
        const quantityInput = form?.querySelector('[name="quantity"]');

        // Prefer live form values so selected variant/quantity are used on PDP.
        const variantId = variantInput?.value || trigger?.dataset.upsellVariantId;
        if (!variantId) return null;

        const rawQuantity = parseInt(trigger?.dataset.upsellQuantity || quantityInput?.value || '1', 10);
        const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? rawQuantity : 1;

        return {
          variantId,
          quantity,
        };
      }

      handleCartUpdate(event) {
        if (!this.open) return;
        this.latestCartData = event?.cart || null;

        const sectionId = this.getUpsellSectionId();
        const sectionHtml = sectionId && event?.cart?.sections ? event.cart.sections[sectionId] : null;
        if (sectionHtml) {
          this.updateCartWrapperFromHtml(sectionHtml, event.cart);
          this.syncStickyBarTotal(event.cart);
          return;
        }

        if (this.cartRefreshTimeout) {
          clearTimeout(this.cartRefreshTimeout);
        }

        this.cartRefreshTimeout = setTimeout(() => {
          this.refreshCartWrapper();
        }, 100);
      }

      async refreshCartWrapper() {
        const sectionUrl = this.getSectionUrl();
        if (!sectionUrl) return;

        try {
          const cacheBustedUrl = `${sectionUrl}${sectionUrl.includes('?') ? '&' : '?'}_=${Date.now()}`;
          const response = await this.fetchWithTimeout(cacheBustedUrl, { cache: 'no-store' });
          
          if (!response.ok) {
            throw new Error(`Failed to refresh cart: ${response.status}`);
          }
          
          const responseText = await response.text();
          this.updateCartWrapperFromHtml(responseText, this.latestCartData);
          this.syncStickyBarTotal(this.latestCartData);
        } catch (error) {
          console.error('Cart refresh error:', error);
          this.showError('Unable to refresh cart. Please reload the page if issues persist.');
        }
      }

      updateCartWrapperFromHtml(htmlText, cartData = null) {
        const currentWrapper = this.querySelector('[data-upsell-cart-wrapper]');
        if (!currentWrapper || !htmlText) return;

        const updatedWrapper = new DOMParser()
          .parseFromString(htmlText, 'text/html')
          .querySelector('[data-upsell-cart-wrapper]');

        if (!updatedWrapper) return;
        this.applyCartDataToFreeShippingGoal(updatedWrapper, cartData);
        this.prepareCartListState(updatedWrapper);
        currentWrapper.innerHTML = updatedWrapper.innerHTML;
        this.syncCurrentItemMirror();
        this.syncSummaryItems();
      }

      /**
       * Prepare cart list state by moving last added item to top and marking as current
       * @param {HTMLElement} scopeElement - Element containing cart list (can be off-DOM)
       */
      prepareCartListState(scopeElement) {
        const cartList = scopeElement.querySelector('.upsell__cart-items');
        if (!cartList) return;

        const cartItems = Array.from(cartList.querySelectorAll('.upsell__cart-item'));
        if (!cartItems.length) return;

        cartItems.forEach((item) => item.classList.remove('is-current-item'));

        let selectedItem =
          this.lastAddedVariantId != null
            ? cartItems.find((item) => item.dataset.variantId === this.lastAddedVariantId)
            : null;

        if (!selectedItem) {
          [selectedItem] = cartItems;
        }
        if (!selectedItem) return;

        if (cartList.firstElementChild !== selectedItem) {
          cartList.prepend(selectedItem);
        }

        selectedItem.classList.add('is-current-item');
      }

      syncCurrentItemMirror() {
        const selectedItemWrapper = this.querySelector('.upsell__is-current-item-wrapper');
        if (!selectedItemWrapper) return;

        selectedItemWrapper.innerHTML = '';
        const selectedItem = this.querySelector('.upsell__cart-items .upsell__cart-item.is-current-item');
        if (!selectedItem) return;

        const selectedItemClone = selectedItem.cloneNode(true);
        selectedItemClone.classList.add('upsell__is-current-item');
        selectedItemWrapper.appendChild(selectedItemClone);
      }

      syncSummaryItems() {
        const summaryDetails = this.querySelector('[data-upsell-cart-summary]');
        const summaryContent = this.querySelector('[data-upsell-summary-content]');
        const summaryCount = this.querySelector('[data-upsell-summary-count]');
        const sourceList = this.querySelector('[data-upsell-cart-wrapper] .upsell__cart-items');
        if (!summaryDetails || !summaryContent || !sourceList) return;

        const remainingItems = Array.from(sourceList.querySelectorAll('.upsell__cart-item')).filter(
          (item) => !item.classList.contains('is-current-item')
        );

        summaryContent.innerHTML = '';
        remainingItems.forEach((item) => {
          const itemClone = item.cloneNode(true);
          itemClone.classList.add('upsell__cart-summary-item');
          itemClone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          itemClone.querySelectorAll('label[for]').forEach((element) => element.removeAttribute('for'));
          summaryContent.appendChild(itemClone);
        });

        if (summaryCount) {
          summaryCount.textContent = `${remainingItems.length}`;
        }
        summaryDetails.toggleAttribute('hidden', remainingItems.length === 0);
      }

      /**
       * Apply cart data to free shipping goal element before rendering to prevent flicker
       * @param {HTMLElement} wrapper - Cart wrapper element (off-DOM)
       * @param {Object} cart - Cart object with total_price
       */
      applyCartDataToFreeShippingGoal(wrapper, cart) {
        if (!wrapper || !cart) return;
        const goalElement = wrapper.querySelector('free-shipping-goal');
        if (!goalElement) return;

        // Keep the same metric as the Liquid snippet to avoid visible jump after re-render.
        const amount = Number(cart.total_price ?? 0);
        goalElement.dataset.cartTotal = `${amount}`;
      }

      syncStickyBarTotal(cart) {
        if (!cart) return;
        const totalEl = this.querySelector('.upsell__sticky-bar-total .totals__subtotal-value');
        if (!totalEl) return;
        totalEl.innerHTML = FoxTheme.Currency.formatMoney(cart.total_price ?? 0, FoxTheme.settings.moneyFormat);
      }

      onUpsellCartActionClick(event) {
        const button = event.target.closest('[data-upsell-cart-action]');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        if (button.classList.contains('btn--loading')) return;

        const action = button.dataset.upsellCartAction;
        if (action === 'close') {
          this.hide();
          return;
        }

        const item = button.closest('.upsell__cart-item');
        if (!item) return;

        const line = Number(item.dataset.line);
        const lineKey = item.dataset.lineKey || '';
        const quantity = Number(item.dataset.quantity);
        if (!line || Number.isNaN(quantity)) return;

        let nextQuantity = quantity;
        if (action === 'increase') {
          nextQuantity = quantity + 1;
        } else if (action === 'decrease') {
          nextQuantity = Math.max(quantity - 1, 0);
        } else if (action === 'remove') {
          nextQuantity = 0;
        }

        this.updateCartLineQuantity({
          line,
          lineKey,
          quantity: nextQuantity,
          triggerElement: button,
        });
      }

      onUpsellCartQuantityChange(event) {
        const input = event.target;
        if (!input?.classList?.contains('quantity__input')) return;
        if (!input.closest('[data-upsell-cart-wrapper], .upsell__is-current-item-wrapper, [data-upsell-cart-summary]')) return;

        const item = input.closest('.upsell__cart-item');
        const line = Number(input.dataset.index || item?.dataset.line);
        const lineKey = input.dataset.lineKey || item?.dataset.lineKey || '';
        if (!line) return;

        const min = Number(input.min || input.dataset.min || 0);
        const max = input.max ? Number(input.max) : null;
        let quantity = Number(input.value);
        if (Number.isNaN(quantity)) quantity = min;
        if (quantity < min) quantity = min;
        if (max !== null && !Number.isNaN(max) && quantity > max) quantity = max;

        input.value = `${quantity}`;
        this.updateCartLineQuantity({
          line,
          lineKey,
          quantity,
          triggerElement: null,
        });
      }

      async updateCartLineQuantity({ line, lineKey = '', quantity, triggerElement = null }) {
        const canShowLoader =
          triggerElement?.classList?.contains('quantity__button') ||
          triggerElement?.classList?.contains('upsell__cart-item-remove');

        if (canShowLoader) {
          triggerElement.classList.add('btn--loading');
        }

        try {
          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
          );

          const payload = {
            quantity,
            sections: sectionsToBundle,
          };

          if (lineKey) {
            payload.id = lineKey;
          } else {
            payload.line = line;
          }

          const body = JSON.stringify(payload);

          const response = await this.fetchWithTimeout(FoxTheme.routes.cart_change_url, {
            ...FoxTheme.utils.fetchConfig(),
            body,
          });

          if (!response.ok) {
            throw new Error(`Failed to update cart: ${response.status}`);
          }

          const cartState = await response.json();
          
          // Update aria-live region for accessibility
          this.announceCartUpdate(null, null, cartState.item_count);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartState });
        } catch (error) {
          console.error('Update cart line error:', error);
          this.showError('Unable to update quantity. Please try again.');
        } finally {
          if (canShowLoader) {
            triggerElement.classList.remove('btn--loading');
          }
        }
      }

      /**
       * Show error message - logs to console and dispatches event for theme integration
       * @param {string} message - Error message to display
       */
      showError(message) {
        console.error('Upsell Modal Error:', message);
        this.renderInlineError(message);
        
        // Dispatch custom event that can be handled by theme if needed
        document.dispatchEvent(new CustomEvent('upsell:error', {
          detail: { message },
          bubbles: true
        }));
      }

      renderInlineError(message) {
        const container = this.querySelector(this.selector);
        if (!container) return;

        let errorElement = container.querySelector(`.${this.inlineErrorClass}`);
        if (!errorElement) {
          errorElement = document.createElement('div');
          errorElement.className = `${this.inlineErrorClass} alert alert--error`;
          errorElement.setAttribute('role', 'alert');
          container.prepend(errorElement);
        }

        errorElement.textContent = message || 'Something went wrong. Please try again.';
      }

      clearInlineError() {
        const errorElement = this.querySelector(`.${this.inlineErrorClass}`);
        if (errorElement) {
          errorElement.remove();
        }
      }

      async fetchWithTimeout(url, options = {}, timeoutMs = this.requestTimeoutMs) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
          return await fetch(url, {
            ...options,
            signal: controller.signal,
          });
        } catch (error) {
          if (error?.name === 'AbortError') {
            throw new Error('Request timed out. Please try again.');
          }
          throw error;
        } finally {
          clearTimeout(timeoutId);
        }
      }

      /**
       * Announce cart updates to screen readers via aria-live region
       * @param {string|null} productTitle - Product title (if product was added)
       * @param {number|null} quantity - Quantity added
       * @param {number} totalItems - Total items in cart
       */
      announceCartUpdate(productTitle, quantity, totalItems) {
        const liveRegion = this.querySelector('[data-upsell-aria-live]');
        if (!liveRegion) return;

        let message = '';
        if (productTitle && quantity) {
          message = `${quantity} ${productTitle} added to cart. Cart now has ${totalItems} item${totalItems !== 1 ? 's' : ''}.`;
        } else {
          message = `Cart updated. ${totalItems} item${totalItems !== 1 ? 's' : ''} in cart.`;
        }

        liveRegion.textContent = message;
        
        // Clear after announcement
        setTimeout(() => {
          liveRegion.textContent = '';
        }, 1000);
      }

      initStickyBarSwiper() {
        if (!window.FoxTheme || !FoxTheme.Carousel) return;

        const swiperEl = this.querySelector('.upsell__sticky-bar-swiper.swiper');
        if (!swiperEl || swiperEl.__upsellStickySwiperInitialized) return;

        const wrapper = swiperEl.querySelector('.swiper-wrapper');
        if (!wrapper) return;

        const slideCount = wrapper.children.length;
        if (!slideCount) return;

        swiperEl.__upsellStickySwiperInitialized = true;

        const options = {
          slidesPerView: 'auto',
          spaceBetween: 40,
          loop: slideCount > 1,
        };

        try {
          const carousel = new FoxTheme.Carousel(swiperEl, options);
          carousel.init();
        } catch (e) {
          console.error('Upsell sticky bar swiper init error:', e);
        }
      }
    }
  );
}